import { ChangeDetectionStrategy, Component, computed, effect, input, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzInputModule } from 'ng-zorro-antd/input';
import { DinamikBilesen } from '@service/fikoyt-ui/decorators/dinamik-bilesen';
import { FikoYTKokBilesenComponent } from '@/src/service/kok-bilesen/kok-bilesen.component';
import { FikoYTIstekKok } from '@/src/service/modules/fikoyt-istek-kok/fikoyt.istek.makinesi.service';

type Durum = 'kritik' | 'uyari' | 'normal';
const DURUM_BILGI: Record<Durum, { etiket: string; renk: string; rgb: string }> = {
  kritik: { etiket: 'Kritik', renk: '#ef4444', rgb: '239,68,68' },
  uyari:  { etiket: 'Uyarı',  renk: '#f59e0b', rgb: '245,158,11' },
  normal: { etiket: 'Normal', renk: '#22c55e', rgb: '34,197,94' },
};

// ---------------------------------------------------------------------------
// genel-stok-pano: kutuk-stok-pano.dinamik-bilesen.ts ile AYNI desen — sadece
// 'kutuk-stok' yerine 'genel-stok' setId'sini hedefler. kutuk-stok-pano dosyasına
// dokunulmamıştır; tamamen ayrı id ile (genel-stok-pano) kayıt olur.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Edit (paylaş kutusu): genel-stok ile paylaşılmış kartları listeler,
// seçilenler ek.seciliUrunler dizisine yazılır.
// ---------------------------------------------------------------------------
@Component({
  selector: 'genel-stok-pano-edit',
  standalone: true,
  imports: [FormsModule, NzInputModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="gpe">
      <div class="gpe-baslik">📦 Panoya eklenecek ürünleri seçin</div>
      <input nz-input nzSize="small" [ngModel]="_filtre()" (ngModelChange)="_filtre.set($event)" placeholder="🔍 Ara..."/>
      @if (_yukleniyor()) { <div class="gpe-bilgi">Yükleniyor...</div> }
      @else if (!_tumUrunler().length) { <div class="gpe-bilgi">Genel stok kartı bulunamadı. Önce genel-stok ile kart paylaşın.</div> }
      @else {
        <div class="gpe-liste">
          @for (u of _filtreliUrunler(); track u._id) {
            <label class="gpe-kalem" [class.gpe-secili]="_secilimi(u._id)">
              <input type="checkbox" [checked]="_secilimi(u._id)" (change)="_toggle(u._id)"/>
              <span class="gpe-ad">{{ u.isim || u.baslik || u._id }}</span>
              @if (u.kalanStok != null) { <span class="gpe-stok">{{ u.kalanStok }} {{ u.birim || 'adet' }}</span> }
            </label>
          }
        </div>
        <div class="gpe-bilgi">{{ _seciliSayi() }} ürün seçildi</div>
      }
    </div>
  `,
  styles: [`
    .gpe { display:flex; flex-direction:column; gap:8px; }
    .gpe-baslik { font-weight:600; font-size:12px; color:var(--py-metin,#333); }
    .gpe-bilgi { font-size:11px; color:var(--py-metin-ikincil,#888); }
    .gpe-liste { display:flex; flex-direction:column; gap:4px; max-height:240px; overflow:auto; }
    .gpe-kalem { display:flex; align-items:center; gap:8px; padding:5px 8px; border-radius:6px;
      border:1px solid var(--py-kenarlik,#e5e7eb); cursor:pointer; font-size:12px; }
    .gpe-kalem:hover { background:rgba(0,0,0,.03); }
    .gpe-secili { border-color:#22c55e; background:rgba(34,197,94,.07); }
    .gpe-ad { flex:1; color:var(--py-metin,#333); }
    .gpe-stok { font-weight:700; font-size:11px; color:var(--py-metin-ikincil,#666); }
  `],
})
export class GenelStokPanoEditComponent extends FikoYTKokBilesenComponent {
  parametreler = model<any>();
  constructor() {
    super();
    effect(() => { this._urunleriCek(); }, { allowSignalWrites: true });
  }

  private _istek: FikoYTIstekKok | null = null;
  _tumUrunler = signal<any[]>([]);
  _yukleniyor = signal(false);
  _filtre = signal('');

  p = computed(() => this.parametreler()?.ek || {});

  /** Seçili ürünleri ek.seciliUrunler dizisinde tutar */
  _seciliUrunler = computed<string[]>(() => {
    const v = this.p()?.seciliUrunler;
    return Array.isArray(v) ? v : [];
  });
  _seciliSayi = computed(() => this._seciliUrunler().length);

  _filtreliUrunler = computed(() => {
    const f = (this._filtre() || '').toLocaleLowerCase('tr');
    if (!f) return this._tumUrunler();
    return this._tumUrunler().filter(u =>
      (u.isim || u.baslik || '').toLocaleLowerCase('tr').includes(f));
  });

  _secilimi(id: string) { return this._seciliUrunler().includes(id); }

  _toggle(id: string) {
    const params = this.parametreler();
    if (!params?.ek) return;
    const mevcut = [...this._seciliUrunler()];
    const i = mevcut.indexOf(id);
    if (i >= 0) mevcut.splice(i, 1); else mevcut.push(id);
    params.ek.seciliUrunler = mevcut;
    this.parametreler.set({ ...params });
  }

  /** genel-stok seti ile paylaşılmış kartları canlı listede göster */
  private async _urunleriCek() {
    this._yukleniyor.set(true);
    try {
      if (!this._istek) this._istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.aggregate' });
      this._istek.parametreler = { aggregate: [
        { $match: { turu: 'KART', silindi: { $ne: true } } },
        { $lookup: { from: 'hareketler', localField: 'veri.icerikler', foreignField: '_id', as: 'ic' } },
        { $unwind: { path: '$ic', preserveNullAndEmptyArrays: false } },
        { $match: { 'ic.ek._secilenSetId': 'genel-stok' } },
        { $project: { _id: 1, isim: '$ic.ek.isim', baslik: '$veri.kurdela.baslik', birim: '$ic.ek.birim' } },
        { $sort: { _id: -1 } },
      ] };
      const cevap: any = await this._istek.isteSync();
      this._tumUrunler.set(cevap?.cevap?.sonuc ?? []);
    } catch (e) {
      console.warn('[GenelStokPano] Ürün listesi alınamadı:', e);
    } finally {
      this._yukleniyor.set(false);
    }
  }
}


// ---------------------------------------------------------------------------
// Görüntü kartı: seçili ürünlerin stok durumunu grid (döşeme) olarak gösterir
// ---------------------------------------------------------------------------
@Component({
  selector: 'genel-stok-pano-kart',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="gpp">
      @if (!_urunler().length) {
        <div class="gpp-bos">📦 Henüz ürün seçilmedi</div>
      } @else {
        <!-- Filtre + görünüm anahtarı -->
        <div class="gpp-araclar">
          <div class="gpp-filtreler">
            <button class="gpp-fbtn" [class.gpp-faktif]="_filtre()==='tumu'" (click)="_filtre.set('tumu')">Tümü ({{ _sayilar().tumu }})</button>
            <button class="gpp-fbtn gpp-kritik" [class.gpp-faktif]="_filtre()==='kritik'" (click)="_filtre.set('kritik')">Kritikler ({{ _sayilar().kritik }})</button>
            <button class="gpp-fbtn gpp-bitmis" [class.gpp-faktif]="_filtre()==='bitmis'" (click)="_filtre.set('bitmis')">Bitenler ({{ _sayilar().bitmis }})</button>
          </div>
          <button class="gpp-gorunum" (click)="_tabloMi.set(!_tabloMi())" [title]="_tabloMi() ? 'Kart görünümü' : 'Tablo görünümü'">
            {{ _tabloMi() ? '🗂' : '📋' }}
          </button>
        </div>

        @if (_filtreli().length === 0) {
          <div class="gpp-bos">Bu filtrede ürün yok</div>
        } @else if (_tabloMi()) {
          <!-- Tablo görünümü -->
          <table class="gpp-table">
            <thead><tr><th>Ürün</th><th>Stok</th><th>Birim</th><th>Limitler</th><th>Durum</th></tr></thead>
            <tbody>
              @for (u of _filtreli(); track u._id) {
                <tr>
                  <td class="gpp-t-ad">{{ u.isim || u.baslik || 'İsimsiz' }}</td>
                  <td class="gpp-t-stok">{{ u._kalan }}</td>
                  <td>{{ u.birim || 'adet' }}</td>
                  <td class="gpp-t-limit">k: {{ u.kritikLimit ?? '—' }} · u: {{ u.uyariLimit ?? '—' }}</td>
                  <td><span class="gpp-rozet" [style.background]="'rgba('+u._rgb+',.15)'" [style.color]="u._renk">{{ u._etiket }}</span></td>
                </tr>
              }
            </tbody>
          </table>
        } @else {
          <!-- Kart (grid) görünümü -->
          <div class="gpp-grid">
            @for (u of _filtreli(); track u._id) {
              <div class="gpp-kutu" [style.--kc]="u._renk">
                <div class="gpp-ust">
                  <span class="gpp-ad">{{ u.isim || u.baslik || 'İsimsiz' }}</span>
                  <span class="gpp-rozet" [style.background]="'rgba('+u._rgb+',.15)'" [style.color]="u._renk">{{ u._etiket }}</span>
                </div>
                <div class="gpp-stok">{{ u._kalan }} <span class="gpp-birim">{{ u.birim || 'adet' }}</span></div>
                <div class="gpp-bar"><div class="gpp-bar-ic" [style.width.%]="u._yuzde" [style.background]="u._renk"></div></div>
              </div>
            }
          </div>
        }
      }
    </div>
  `,
  styles: [`
    .gpp { display:block; }
    .gpp-bos { padding:16px; text-align:center; font-size:12px; color:var(--py-metin-ikincil,#999);
      border:1px dashed var(--py-kenarlik,#e5e7eb); border-radius:8px; }
    .gpp-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(130px, 1fr)); gap:8px; }
    .gpp-araclar { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:6px; }
    .gpp-filtreler { display:flex; gap:4px; flex-wrap:wrap; }
    .gpp-fbtn { padding:3px 10px; font-size:11px; font-weight:600; cursor:pointer;
      border:1px solid var(--py-kenarlik,#e5e7eb); border-radius:999px;
      background:var(--py-arkaplan,#fff); color:var(--py-metin,#444); transition:background .15s; }
    .gpp-fbtn:hover { background:rgba(0,0,0,.04); }
    .gpp-fbtn.gpp-kritik.gpp-faktif { background:#ef4444; border-color:#ef4444; color:#fff; }
    .gpp-fbtn.gpp-bitmis.gpp-faktif { background:#f59e0b; border-color:#f59e0b; color:#fff; }
    .gpp-fbtn.gpp-faktif:not(.gpp-kritik):not(.gpp-bitmis) { background:#22c55e; border-color:#22c55e; color:#fff; }
    .gpp-gorunum { padding:3px 8px; font-size:13px; cursor:pointer; border-radius:6px;
      border:1px solid var(--py-kenarlik,#e5e7eb); background:var(--py-arkaplan,#fff); }
    .gpp-gorunum:hover { background:rgba(0,0,0,.04); }
    .gpp-table { width:100%; border-collapse:collapse; font-size:12px; }
    .gpp-table th { text-align:left; font-size:10px; font-weight:700; text-transform:uppercase;
      letter-spacing:.05em; color:var(--py-metin-ucuncul,#999); padding:4px 8px;
      border-bottom:1px solid var(--py-kenarlik,#e5e7eb); }
    .gpp-table td { padding:5px 8px; border-bottom:1px solid var(--py-kenarlik,#f0f0f0);
      color:var(--py-metin,#333); }
    .gpp-table tr:hover td { background:rgba(0,0,0,.02); }
    .gpp-t-ad { font-weight:700; }
    .gpp-t-stok { font-weight:800; font-family:monospace; font-size:13px; }
    .gpp-t-limit { font-size:11px; color:var(--py-metin-ikincil,#888); }
    .gpp-kutu { display:flex; flex-direction:column; gap:4px; padding:8px 10px;
      border:1px solid var(--py-kenarlik,#e5e7eb); border-left:3px solid var(--kc,#22c55e);
      border-radius:8px; background:var(--py-arkaplan,#fff); }
    .gpp-ust { display:flex; align-items:center; justify-content:space-between; gap:4px; }
    .gpp-ad { font-size:12px; font-weight:700; color:var(--py-metin,#333);
      overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .gpp-rozet { font-size:9px; font-weight:700; text-transform:uppercase; padding:1px 5px; border-radius:4px; white-space:nowrap; }
    .gpp-stok { font-size:18px; font-weight:800; color:var(--py-metin,#222); }
    .gpp-birim { font-size:10px; font-weight:500; color:var(--py-metin-ikincil,#888); }
    .gpp-bar { height:4px; border-radius:2px; background:rgba(0,0,0,.06); overflow:hidden; }
    .gpp-bar-ic { height:100%; border-radius:2px; transition:width .5s; }
  `],
})
export class GenelStokPanoKartComponent extends FikoYTKokBilesenComponent {
  parametreler = input<any>();
  constructor() {
    super();
    effect(() => {
      const ids = this._seciliIds();
      if (ids.length) this._stoklariCek(ids);
    });
  }

  private _istek: FikoYTIstekKok | null = null;
  private _stoklar = signal<any[]>([]);

  _seciliIds = computed<string[]>(() => {
    const v = this.parametreler()?.ek?.seciliUrunler;
    return Array.isArray(v) ? v : [];
  });

  _urunler = computed<any[]>(() => this._stoklar());

  /** Filtre + görünüm durumu */
  _filtre = signal<'tumu' | 'kritik' | 'bitmis'>('tumu');
  _tabloMi = signal(false);

  _sayilar = computed(() => {
    const l = this._urunler();
    return {
      tumu: l.length,
      kritik: l.filter(u => u._etiket === 'Kritik').length,
      bitmis: l.filter(u => u._kalan <= 0).length,
    };
  });

  _filtreli = computed<any[]>(() => {
    const f = this._filtre();
    const l = this._urunler();
    if (f === 'kritik') return l.filter(u => u._etiket === 'Kritik');
    if (f === 'bitmis') return l.filter(u => u._kalan <= 0);
    return l;
  });


  /** Seçili her ürünün canlı stok durumunu tek aggregate ile çeker */
  private async _stoklariCek(ids: string[]) {
    try {
      if (!this._istek) this._istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.aggregate' });
      this._istek.parametreler = { aggregate: [
        { $match: { _id: { $in: ids }, silindi: { $ne: true } } },
        { $lookup: { from: 'hareketler', let: { pid: '$_id' }, pipeline: [
          { $match: { $expr: { $eq: ['$_uid', '$$pid'] }, silindi: { $ne: true } } },
          { $lookup: { from: 'hareketler', localField: 'veri.icerikler', foreignField: '_id', as: 'ic' } },
          { $unwind: { path: '$ic', preserveNullAndEmptyArrays: false } },
          { $match: { 'ic.ek._secilenSetId': 'genel-stok.hareket' } },
          { $project: {
            tip: { $ifNull: ['$ic.ek.tip', { $let: { vars: { p: { $arrayElemAt: [ { $filter: { input: { $ifNull: ['$ic.ek.parametreler', []] }, as: 'x', cond: { $eq: ['$$x.ismi', 'tip'] } } }, 0 ] } }, in: { $ifNull: ['$$p.degeri', 'giris'] } } } ] },
            miktar: { $toDouble: { $ifNull: ['$ic.ek.miktar', { $let: { vars: { p: { $arrayElemAt: [ { $filter: { input: { $ifNull: ['$ic.ek.parametreler', []] }, as: 'x', cond: { $eq: ['$$x.ismi', 'miktar'] } } }, 0 ] } }, in: { $ifNull: ['$$p.degeri', 0] } } } ] } },
          } },
        ], as: '_h' } },
        { $addFields: {
          _kalan: { $round: [{ $reduce: {
            input: '$_h', initialValue: 0,
            in: { $switch: { branches: [
              { case: { $eq: ['$$this.tip', 'giris'] }, then: { $add: ['$$value', '$$this.miktar'] } },
              { case: { $in: ['$$this.tip', ['cikis', 'fire']] }, then: { $subtract: ['$$value', '$$this.miktar'] } },
              { case: { $eq: ['$$this.tip', 'sayim'] }, then: '$$value' },
            ], default: '$$value' } } },
          }, 0] },
        } },
        { $lookup: { from: 'hareketler', localField: 'veri.icerikler', foreignField: '_id', as: 'ic2' } },
        { $unwind: { path: '$ic2', preserveNullAndEmptyArrays: true } },
        { $match: { $or: [ { ic2: null }, { 'ic2.ek._secilenSetId': 'genel-stok' } ] } },
        { $project: {
          _id: 1, isim: '$ic2.ek.isim', baslik: '$veri.kurdela.baslik',
          birim: '$ic2.ek.birim', uyariLimit: '$ic2.ek.uyariLimit', kritikLimit: '$ic2.ek.kritikLimit',
          _kalan: 1,
        } },
      ] };
      const cevap: any = await this._istek.isteSync();
      const liste: any[] = (cevap?.cevap?.sonuc ?? []).map((u: any) => {
        const kalan = +(u._kalan ?? 0);
        const kritik = +(u.kritikLimit || 0);
        const uyari = +(u.uyariLimit || 0);
        const durum: Durum = (kritik && kalan <= kritik) ? 'kritik' : (uyari && kalan <= uyari) ? 'uyari' : 'normal';
        const b = DURUM_BILGI[durum];
        const tavan = uyari > 0 ? uyari * 1.6 : Math.max(kalan, 1);
        return {
          ...u,
          _kalan: kalan,
          _etiket: b.etiket, _renk: b.renk, _rgb: b.rgb,
          _yuzde: Math.max(4, Math.min(100, (kalan / tavan) * 100)),
        };
      });
      // En düşük stok başa gelecek şekilde sırala
      liste.sort((a, b) => a._kalan - b._kalan);
      this._stoklar.set(liste);
    } catch (e) {
      console.warn('[GenelStokPano] Stok bilgisi alınamadı:', e);
    }
  }
}

// ---------------------------------------------------------------------------
// genel-stok-pano DinamikBilesen kaydı
// ---------------------------------------------------------------------------
@DinamikBilesen({
  id: 'genel-stok-pano',
  baslik: 'Genel Stok Panosu',
  aciklama: 'Seçilen ürünlerin stok durumunu grid olarak gösterir',
  ikon: 'grid',
  kategori: 'genel-stok',
})
class GenelStokPano {
  paylas_kutusu         = GenelStokPanoEditComponent;
  paylasilmis_kart      = GenelStokPanoKartComponent;
  paylasilmis_kart_sade = GenelStokPanoKartComponent;
}
